import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Github, Search, Loader2, AlertTriangle, CheckCircle2, XCircle,
  FolderGit2, FileCode, Eye, RefreshCw, ChevronDown, ChevronRight,
  Shield, Lock, Unlock, Star, GitFork, Clock, BookOpen
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';

const SeverityBadge = ({ severity }) => {
  const colors = {
    critical: 'bg-red-600 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-yellow-500 text-black',
    low: 'bg-blue-500 text-white',
    info: 'bg-gray-500 text-white',
  };
  return <Badge className={`${colors[severity] || colors.info} text-xs`}>{severity}</Badge>;
};

const FindingCard = ({ finding, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg border transition-colors">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400 w-6">#{index + 1}</span>
            <SeverityBadge severity={finding.severity} />
            <span className="text-sm font-medium text-left">{finding.title || finding.rule_id}</span>
          </div>
          <div className="flex items-center gap-2">
            {finding.file_path && <span className="text-xs text-gray-400 font-mono">{finding.file_path}</span>}
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="p-4 ml-9 space-y-3 bg-gray-50 rounded-b-lg border-x border-b">
          <p className="text-sm text-gray-700">{finding.description}</p>
          {finding.file_path && (
            <div>
              <Label className="text-xs text-gray-500">File</Label>
              <p className="text-xs font-mono bg-white p-2 rounded border">{finding.file_path}{finding.line_number ? `:${finding.line_number}` : ''}</p>
            </div>
          )}
          {finding.code_snippet && (
            <div>
              <Label className="text-xs text-gray-500">Code</Label>
              <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded font-mono overflow-x-auto">{finding.code_snippet}</pre>
            </div>
          )}
          {finding.remediation && (
            <div>
              <Label className="text-xs text-gray-500">Remediation</Label>
              <p className="text-sm text-gray-700">{finding.remediation}</p>
            </div>
          )}
          <div className="flex gap-2 text-xs">
            {finding.cwe_id && <Badge variant="outline">CWE: {finding.cwe_id}</Badge>}
            {finding.owasp_id && <Badge variant="outline">OWASP: {finding.owasp_id}</Badge>}
            {finding.category && <Badge variant="secondary">{finding.category}</Badge>}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default function GitHubScanPage() {
  const [tab, setTab] = useState('scan');
  const [ghStatus, setGhStatus] = useState(null);
  const [repos, setRepos] = useState([]);
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [reposLoading, setReposLoading] = useState(false);
  const [selectedScan, setSelectedScan] = useState(null);

  const [scanForm, setScanForm] = useState({ owner: '', repo: '', ref: 'main', max_files: 100 });

  const checkStatus = useCallback(async () => {
    try {
      const res = await api.get('/integrations/github/status');
      setGhStatus(res.data);
    } catch {
      setGhStatus({ configured: false });
    }
  }, []);

  const fetchScans = useCallback(async () => {
    try {
      const res = await api.get('/integrations/github/scans');
      setScans(Array.isArray(res.data) ? res.data : []);
    } catch {
      setScans([]);
    }
  }, []);

  const fetchRepos = useCallback(async () => {
    if (!ghStatus?.configured) return;
    setReposLoading(true);
    try {
      const res = await api.get('/integrations/github/repositories');
      setRepos(Array.isArray(res.data) ? res.data : []);
    } catch {
      setRepos([]);
    } finally {
      setReposLoading(false);
    }
  }, [ghStatus]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await checkStatus();
      await fetchScans();
      setLoading(false);
    };
    init();
  }, [checkStatus, fetchScans]);

  const handleScan = async () => {
    if (!scanForm.owner || !scanForm.repo) {
      toast.error('Owner and repository are required');
      return;
    }
    setScanning(true);
    try {
      const res = await api.post('/integrations/github/scan', scanForm);
      toast.success(`Scan completed: ${res.data.total_findings || 0} findings`);
      fetchScans();
      setTab('history');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const selectRepoForScan = (repo) => {
    const parts = repo.full_name?.split('/') || [];
    setScanForm({ ...scanForm, owner: parts[0] || '', repo: parts[1] || repo.name, ref: repo.default_branch || 'main' });
    setTab('scan');
    toast.success(`Selected ${repo.full_name}`);
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="github-scan-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Github className="w-6 h-6" /> GitHub Repository Scanning
          </h1>
          <p className="text-sm text-gray-500 mt-1">Scan private repositories for security vulnerabilities</p>
        </div>
        <div className="flex items-center gap-2">
          {ghStatus?.configured ? (
            <Badge className="bg-green-600"><CheckCircle2 className="w-3 h-3 mr-1" /> Connected</Badge>
          ) : (
            <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Not Configured</Badge>
          )}
          <Button variant="outline" size="sm" onClick={() => { checkStatus(); fetchScans(); }} data-testid="refresh-gh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {!ghStatus?.configured && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">GitHub integration not configured</p>
              <p className="text-sm text-yellow-700">Go to Settings &gt; Integrations &gt; Scanner tab to configure your GitHub token and start scanning private repos.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="scan" data-testid="scan-tab">Scan Repository</TabsTrigger>
          <TabsTrigger value="repos" data-testid="repos-tab" onClick={fetchRepos}>Browse Repositories</TabsTrigger>
          <TabsTrigger value="history" data-testid="history-tab">Scan History ({scans.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="scan" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Search className="w-5 h-5" /> Scan a Repository</CardTitle>
              <CardDescription>Enter the repository details to scan for security vulnerabilities</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Owner / Organization</Label>
                  <Input value={scanForm.owner} onChange={e => setScanForm({...scanForm, owner: e.target.value})} placeholder="octocat" data-testid="owner-input" />
                </div>
                <div>
                  <Label>Repository</Label>
                  <Input value={scanForm.repo} onChange={e => setScanForm({...scanForm, repo: e.target.value})} placeholder="my-app" data-testid="repo-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Branch / Ref</Label>
                  <Input value={scanForm.ref} onChange={e => setScanForm({...scanForm, ref: e.target.value})} placeholder="main" data-testid="ref-input" />
                </div>
                <div>
                  <Label>Max Files to Scan</Label>
                  <Input type="number" value={scanForm.max_files} onChange={e => setScanForm({...scanForm, max_files: parseInt(e.target.value) || 100})} data-testid="max-files-input" />
                </div>
              </div>
              <Button className="w-full" onClick={handleScan} disabled={scanning || !ghStatus?.configured} data-testid="start-scan-btn">
                {scanning ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Scanning...</>
                ) : (
                  <><Shield className="w-4 h-4 mr-2" /> Start Security Scan</>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="repos" className="mt-4">
          {reposLoading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
          ) : repos.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FolderGit2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">{ghStatus?.configured ? 'No repositories found' : 'Configure GitHub integration first'}</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {repos.map((repo) => (
                <Card key={repo.full_name} className="hover:border-blue-300 transition-colors" data-testid={`repo-card-${repo.name}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {repo.private ? <Lock className="w-4 h-4 text-yellow-600" /> : <Unlock className="w-4 h-4 text-green-600" />}
                      <div>
                        <h3 className="font-medium">{repo.full_name}</h3>
                        <p className="text-xs text-gray-500 mt-0.5">{repo.description || 'No description'}</p>
                        <div className="flex gap-3 mt-1 text-xs text-gray-400">
                          {repo.language && <span className="flex items-center gap-1"><FileCode className="w-3 h-3" />{repo.language}</span>}
                          {repo.stargazers_count != null && <span className="flex items-center gap-1"><Star className="w-3 h-3" />{repo.stargazers_count}</span>}
                          {repo.forks_count != null && <span className="flex items-center gap-1"><GitFork className="w-3 h-3" />{repo.forks_count}</span>}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" onClick={() => selectRepoForScan(repo)} data-testid={`scan-repo-${repo.name}`}>
                      <Shield className="w-4 h-4 mr-1" /> Scan
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          {scans.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No scan history</p>
                <p className="text-sm text-gray-400 mt-1">Scan a repository to see results here</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {scans.map((scan, idx) => (
                <Card key={scan.id || scan.scan_id || `scan-${idx}`} data-testid={`scan-history-${idx}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <h3 className="font-medium">{scan.repository}</h3>
                        <p className="text-xs text-gray-500">
                          Scanned {scan.files_scanned || 0} files on branch {scan.ref || 'main'}
                          {scan.scan_timestamp && ` - ${new Date(scan.scan_timestamp).toLocaleString()}`}
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Badge className="bg-red-600 text-xs">{scan.severity_counts?.critical || 0} Critical</Badge>
                        <Badge className="bg-orange-500 text-xs">{scan.severity_counts?.high || 0} High</Badge>
                        <Badge className="bg-yellow-500 text-black text-xs">{scan.severity_counts?.medium || 0} Medium</Badge>
                        <Badge className="bg-blue-500 text-xs">{scan.severity_counts?.low || 0} Low</Badge>
                      </div>
                    </div>
                    {scan.findings?.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <p className="text-xs text-gray-500 font-medium">Findings ({scan.total_findings || scan.findings.length})</p>
                        {scan.findings.slice(0, selectedScan === idx ? 100 : 5).map((f, fi) => (
                          <FindingCard key={f.id || f.rule_id || `finding-${fi}`} finding={f} index={fi} />
                        ))}
                        {scan.findings.length > 5 && selectedScan !== idx && (
                          <Button variant="outline" size="sm" onClick={() => setSelectedScan(idx)} className="w-full" data-testid={`show-all-${idx}`}>
                            Show all {scan.findings.length} findings
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
